import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { filter, switchMap, take, takeWhile } from 'rxjs/operators';

import { AsyncJobSelectors, AsyncJobStatus } from '@core/async-job-data';
import { BulkActionType } from '@core/bulk-actions-legacy';
import type { BulkAction } from '@core/bulk-actions-legacy';
import type { BulkOfferResponse } from '@core/bulk-actions-types';
import { filterBoolean } from '@core/common-legacy';
import type { Requisition } from '@core/requisitions-types';
import { generateUuid } from '@core/utils-legacy';
import { RequisitionsBulkActions, RequisitionsBulkSelectors } from '@recruitee/requisitions-data';

import { RequisitionsDialogsService } from './requisitions-dialogs.service';

@Injectable()
export class RequisitionsBulkActionsExecutorService {
  private readonly store = inject(Store);
  private readonly requisitionsDialogs = inject(RequisitionsDialogsService);

  public getExecutionStream(
    action: BulkAction,
    requisitions: Requisition[],
  ): Observable<BulkOfferResponse | boolean> {
    const correlationId = generateUuid();
    const trackAsyncJob$ = this.trackByCorrelationId(correlationId);
    const ids = requisitions.map(req => req.id);
    const actionPayload = { correlationId, params: { ids } };

    return this.confirm$(action, requisitions).pipe(
      switchMap(confirmed => {
        if (confirmed) {
          this.dispatchAction(action.id, actionPayload);
          return trackAsyncJob$;
        } else {
          return of(false);
        }
      }),
    );
  }

  private confirm$(action: BulkAction, requisitions: Requisition[]): Observable<boolean> {
    if (action.id === BulkActionType.Archive) {
      return this.requisitionsDialogs.confirmBulkArchive$(requisitions);
    }
    if (action.id === BulkActionType.RetrieveArchived) {
      return this.requisitionsDialogs.confirmBulkRetrieve$(requisitions);
    }
    return of(true);
  }

  private trackByCorrelationId(correlationId: string) {
    return this.store.select(RequisitionsBulkSelectors.byId(correlationId)).pipe(
      filterBoolean,
      filter(entity => !!entity.uuid),
      take(1),
      switchMap(({ uuid }) => this.store.select(AsyncJobSelectors.byId(uuid))),
      filterBoolean,
      takeWhile(job => job.status !== AsyncJobStatus.FINISHED, true),
    );
  }

  private dispatchAction(
    actionType: BulkActionType,
    payload: { correlationId: string; params: { ids: number[] } },
  ): void {
    switch (actionType) {
      case BulkActionType.Follow:
        this.store.dispatch(RequisitionsBulkActions.follow({ payload }));
        break;
      case BulkActionType.Unfollow:
        this.store.dispatch(RequisitionsBulkActions.unfollow({ payload }));
        break;
      case BulkActionType.Archive:
        this.store.dispatch(RequisitionsBulkActions.archive({ payload }));
        break;
      case BulkActionType.RetrieveArchived:
        this.store.dispatch(RequisitionsBulkActions.retrieve({ payload }));
        break;
      default:
        break;
    }
  }
}
